import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ButtonLink } from '@/components/ui/Button'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
}

/**
 * Keeps a render error in one route from blanking the whole site.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // No user-supplied content is logged here — only the failure itself.
    console.error('Unhandled render error:', error.message, info.componentStack)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="shell flex min-h-[60vh] flex-col items-start justify-center py-20">
        <p className="eyebrow mb-3">Something went wrong</p>
        <h1 className="text-balance text-[28px] font-extrabold leading-tight tracking-[-0.02em] sm:text-[36px]">
          This page failed to render.
        </h1>
        <p className="mt-4 max-w-measure text-[15px] leading-[1.75] text-muted">
          The failure has been logged. Reloading usually clears it; if it does not, the rest of the
          site is still reachable.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <ButtonLink to="/" size="lg">
            Back to home
          </ButtonLink>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex min-h-[48px] items-center rounded-control border border-line bg-white px-5 text-[13.5px] font-bold text-muted transition-colors hover:border-edge-soft hover:text-ink"
          >
            Reload
          </button>
        </div>
      </div>
    )
  }
}
